"use client"

import { useEffect, useState } from "react"
import Layout from "../components/Layout"
import Card from "../components/Card"
import Button from "../components/Button"
import Alert from "../components/Alert"
import { Search, Filter, Download } from "react-feather"
import MedicineInfoModal from "../components/MedicineInfoModal"
import MedicineHistoryModal from "../components/MedicineHistoryModal"
import { useParams } from "react-router-dom"



const Medicine = () => {
  const [medicines, setMedicines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [alert, setAlert] = useState(null)
  const [selectedMedicine, setSelectedMedicine] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [historyMedicine, setHistoryMedicine] = useState(null)
  const { userId } = useParams()
  const [userRole, setUserRole] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';
  



const fetchMedicines = async (page = 1, search = "", type = "") => {
  try {
    setLoading(true);
    let token = localStorage.getItem("userToken");

    // Fallback to token from navigation state if not in localStorage
    

    if (!token) {
      setAlert({
        type: "error",
        message: "You must be logged in to view medicines",
      });
      return;
    }

    // Fetch user info to get role
    const userInfoRes = await fetch(`${API_BASE_URL}/api/auth/user/${userId}`);
    const userInfo = await userInfoRes.json();
    const role = userInfo.user?.role?.toLowerCase();
    setUserRole(role);
    let baseUrl = "";
    if (role === "pharmacy") {
      baseUrl = `${API_BASE_URL}/api/medicines/my/${userId}`;
    } else if (role === "supplier") {
      baseUrl = `${API_BASE_URL}/api/imports/my/${userId}`;
    } else {
      setMedicines([]);
      setTotalPages(1);
      setAlert({ type: "info", message: "This user is not a pharmacy or supplier." });
      return;
    }

    const url = new URL(baseUrl);
    url.searchParams.append("page", page);
    url.searchParams.append("limit", 10);
    if (search) url.searchParams.append("search", search);
    if (type) url.searchParams.append("type", type);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch medicines");
    }

    setMedicines(data.data || []);
    setTotalPages(data.totalPages || 1);
  } catch (error) {
    setError(error.message);
    setAlert({
      type: "error",
      message: error.message,
    });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchMedicines(currentPage, searchTerm, typeFilter)
    // eslint-disable-next-line
  }, [currentPage, userId])


  const handleSearch = () => {
    setCurrentPage(1)
    fetchMedicines(1, searchTerm, typeFilter)
  }

  const handleFilterChange = (e) => {
    const type = e.target.value
    setTypeFilter(type)
    setCurrentPage(1)
    fetchMedicines(1, searchTerm, type)
  }

  const handleExport = () => {
    // This would typically call an API endpoint that returns a CSV/Excel file
    setAlert({
      type: "info",
      message: "Export functionality would be implemented here",
    })
  }

  const handleMedicineClick = (medicine) => {
    setSelectedMedicine(medicine)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedMedicine(null)
  }

  const openHistory = (medicine, e) => {
    if (e) e.stopPropagation()
    setHistoryMedicine(medicine)
    setIsHistoryOpen(true)
  }

  const closeHistory = () => {
    setIsHistoryOpen(false)
    setHistoryMedicine(null)
  }

  const columns = [
    {
      header: "No",
      accessor: "",
      render: (row, index) => (currentPage - 1) * 10 + index + 1,
      style: { width: "50px" },
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Content",
      accessor: "content",
    },
    {
      header: "Measurement",
      accessor: "measurementUnit",
    },
    {
      header: "Country",
      accessor: "country",
    },
    {
      header: "Type",
      accessor: "type",
      render: (row) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full capitalize bg-blue-100 text-blue-800">
          {row.type}
        </span>
      ),
    },
    {
      header: "Expire Date",
      accessor: "expireDate",
      render: (row) => new Date(row.expireDate).toLocaleDateString(),
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (row) => (
        <div className="flex gap-2">
          <Button variant="secondary" onClick={(e) => openHistory(row, e)}>History</Button>
        </div>
      ),
      style: { width: "140px" },
    },
  ]

  return (
    <Layout title="Medicines">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search medicines..."
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <div className="flex items-center">
              <Filter className="w-4 h-4 text-gray-500 mr-2" />
              <select className="border rounded-lg px-4 py-2" value={typeFilter} onChange={handleFilterChange}>
                <option value="">All Types</option>
                <option value="tablet">Tablet</option>
                <option value="liquid">Liquid</option>
                <option value="capsule">Capsule</option>
                <option value="injection">Injection</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSearch}>Search</Button>
            <Button variant="secondary" onClick={handleExport} className="flex items-center gap-1">
              <Download className="w-4 h-4" />
              Export
            </Button>
          </div>
        </div>

        <div className="relative overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th key={index} className="px-6 py-3" style={column.style}>
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : medicines.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-4 text-center">
                    No medicines found
                  </td>
                </tr>
              ) : (
                medicines.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                    onClick={() => handleMedicineClick(row)}
                  >
                    {columns.map((column, colIndex) => (
                      <td key={colIndex} className="px-6 py-4" style={column.style}>
                        {column.render ? column.render(row, rowIndex) : row[column.accessor]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex rounded-md shadow">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border-t border-b border-gray-300 bg-white text-sm font-medium ${
                      currentPage === pageNum
                        ? "bg-blue-50 text-blue-600 border-blue-500"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </Card>
      <MedicineInfoModal medicine={selectedMedicine} isOpen={isModalOpen} onClose={handleCloseModal} />
      <MedicineHistoryModal medicine={historyMedicine} isOpen={isHistoryOpen} onClose={closeHistory} />
    </Layout>
  )
}

export default Medicine
